import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  IndianRupee,
  Plus,
  Check,
  Clock,
  BarChart3,
  Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { HaafPaymentsTable } from "./components/haaf-payments-table";
import { AddHaafPaymentForm } from "./components/add-haaf-payment-form";
import { HaafReceiptModal } from "@/components/haaf-payment/HaafReceiptModal";
import {
  useHaafPayments,
  useVerifyHaafPayment,
  useUnverifyHaafPayment,
  useDeleteHaafPayment,
  useHaafPaymentBatchStats,
  useHaafPaymentChapterStats,
} from "@/api/haaf-payments";
import { Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { isWithinInterval, startOfDay, endOfDay } from "date-fns";
import { currencies } from "@/lib/constants";
import { useDebounce } from "@/hooks/useDebounce";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import * as XLSX from "xlsx";

const ITEMS_PER_LOAD = 20;

export default function HaafPayments() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState("all");
  const [sortOrder, setSortOrder] = useState("desc");
  const [dateRange, setDateRange] = useState({
    from: null,
    to: null,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [displayedItemsCount, setDisplayedItemsCount] =
    useState(ITEMS_PER_LOAD);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Determine active tab from URL - check for exact path match
  const currentPath = location.pathname;
  const activeTab = currentPath.endsWith("/statistics")
    ? "statistics"
    : "payments";

  const { data: payments, isLoading } = useHaafPayments();
  const { data: batchStats, isLoading: isBatchStatsLoading } =
    useHaafPaymentBatchStats();
  const { data: chapterStats, isLoading: isChapterStatsLoading } =
    useHaafPaymentChapterStats();
  // Handle tab changes via URL navigation
  const handleTabChange = useCallback(
    (newTab) => {
      if (newTab === "payments") {
        navigate("/admin/haaf-payments");
      } else if (newTab === "statistics") {
        navigate("/admin/haaf-payments/statistics");
      }
    },
    [navigate],
  );

  const verifyMutation = useVerifyHaafPayment();
  const unverifyMutation = useUnverifyHaafPayment();
  const deleteMutation = useDeleteHaafPayment();
  const [editPayment, setEditPayment] = useState(null);
  const [receiptPayment, setReceiptPayment] = useState(null);

  const handleVerificationToggle = useCallback(
    (payment) => {
      if (payment.verified) {
        unverifyMutation.mutate(payment._id);
      } else {
        verifyMutation.mutate(payment._id);
      }
    },
    [verifyMutation, unverifyMutation],
  );

  const handleDelete = useCallback(
    (paymentId) => {
      if (window.confirm("Are you sure you want to delete this payment?")) {
        deleteMutation.mutate(paymentId);
      }
    },
    [deleteMutation],
  );

  const handleEdit = useCallback((payment) => {
    setEditPayment(payment);
  }, []);

  const handleDownloadReceipt = useCallback((payment) => {
    setReceiptPayment(payment);
  }, []);

  const handleReceiptClose = useCallback(() => {
    setSelectedReceipt(null);
    setReceiptModalOpen(false);
  }, []);

  // Get unique batch values for filter
  const batches = useMemo(() => {
    if (!payments) return [];
    const batchSet = new Set(payments.map((payment) => payment.batch));
    return ["all", ...Array.from(batchSet)];
  }, [payments]);

  // Optimize filtering and sorting with useMemo
  const { filteredPayments, displayedPayments, hasMore } = useMemo(() => {
    if (!payments)
      return { filteredPayments: [], displayedPayments: [], hasMore: false };

    // Filter payments
    const filtered = payments.filter((payment) => {
      // Search filter
      if (debouncedSearchQuery) {
        const query = debouncedSearchQuery.toLowerCase();
        const matchesSearch =
          payment.paymentId?.toLowerCase().includes(query) ||
          payment.payee?.toLowerCase().includes(query) ||
          payment.phoneNumber?.toLowerCase().includes(query) ||
          payment.emailAddress?.toLowerCase().includes(query) ||
          payment.chapter?.chapterName?.toLowerCase().includes(query) ||
          payment.batch?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      // Batch filter
      if (selectedBatch !== "all" && payment.batch !== selectedBatch) {
        return false;
      }

      // Date filter
      if (dateRange.from && dateRange.to) {
        const paymentDate = new Date(payment.createdAt);
        return isWithinInterval(paymentDate, {
          start: startOfDay(dateRange.from),
          end: endOfDay(dateRange.to),
        });
      }

      return true;
    });

    // Sort payments
    const sorted = [...filtered].sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return sortOrder === "desc" ? dateB - dateA : dateA - dateB;
    });

    // Get displayed items based on current count
    const displayed = sorted.slice(0, displayedItemsCount);
    const hasMore = sorted.length > displayedItemsCount;

    return {
      filteredPayments: filtered,
      displayedPayments: displayed,
      hasMore,
    };
  }, [
    payments,
    debouncedSearchQuery,
    selectedBatch,
    dateRange,
    sortOrder,
    displayedItemsCount,
  ]);

  // Download Excel function - defined after filteredPayments
  const downloadExcel = useCallback(async () => {
    if (!filteredPayments || filteredPayments.length === 0) {
      alert("No data to download");
      return;
    }

    setIsDownloadingExcel(true);

    try {
      // Small delay to show loading state
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Prepare data for Excel
      const excelData = filteredPayments.map((payment) => ({
        "Payment ID": payment.paymentId,
        Payee: payment.payee || "",
        Phone: payment.phoneNumber || "",
        Batch: payment.batch || "",
        Amount: payment.amount,
        Currency: payment.currency || "INR",
        Chapter: payment.chapter?.chapterName || "",
        Status: payment.verified ? "Verified" : "Pending",
        Date: payment.createdAt
          ? new Date(payment.createdAt).toLocaleDateString()
          : "",
        "Created By": payment.createdBy?.user?.email || "",
      }));

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      const columnWidths = [
        { wch: 15 }, // Payment ID
        { wch: 25 }, // Payee
        { wch: 15 }, // Phone
        { wch: 10 }, // Batch
        { wch: 12 }, // Amount
        { wch: 8 }, // Currency
        { wch: 20 }, // Chapter
        { wch: 10 }, // Status
        { wch: 12 }, // Date
        { wch: 25 }, // Created By
      ];
      worksheet["!cols"] = columnWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, "HAF Payments");

      // Generate filename with current date
      const currentDate = new Date().toISOString().split("T")[0];
      const filename = `haf-payments-${currentDate}.xlsx`;

      // Download file
      XLSX.writeFile(workbook, filename);

      // Show success message
      alert(
        `Excel file downloaded successfully!\nFilename: ${filename}\nTotal records: ${excelData.length}`,
      );
    } catch (error) {
      console.error("Error downloading Excel:", error);
      alert("Error downloading Excel file. Please try again.");
    } finally {
      setIsDownloadingExcel(false);
    }
  }, [filteredPayments, setIsDownloadingExcel]);

  // Load more items
  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    // Simulate loading delay for better UX
    setTimeout(() => {
      setDisplayedItemsCount((prev) => prev + ITEMS_PER_LOAD);
      setIsLoadingMore(false);
    }, 300);
  }, [isLoadingMore, hasMore]);

  // Window scroll event handler
  const handleScroll = useCallback(() => {
    // Only handle scroll on payments tab
    if (activeTab !== "payments") return;

    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.scrollY || document.documentElement.scrollTop;

    const isNearBottom = windowHeight + scrollTop >= documentHeight - 200;

    if (isNearBottom && hasMore && !isLoadingMore) {
      loadMore();
    }
  }, [hasMore, isLoadingMore, loadMore, activeTab]);

  // Add window scroll listener
  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Reset displayed items when filters change
  const handleBatchChange = useCallback((newBatch) => {
    setSelectedBatch(newBatch);
    setDisplayedItemsCount(ITEMS_PER_LOAD);
  }, []);

  const handleSortChange = useCallback((newSort) => {
    setSortOrder(newSort);
    setDisplayedItemsCount(ITEMS_PER_LOAD);
  }, []);

  const handleDateRangeChange = useCallback((field, date) => {
    setDateRange((prev) => ({ ...prev, [field]: date }));
    setDisplayedItemsCount(ITEMS_PER_LOAD);
  }, []);

  // Reset when search changes
  useEffect(() => {
    setDisplayedItemsCount(ITEMS_PER_LOAD);
  }, [debouncedSearchQuery]);

  // Calculate stats with useMemo for better performance
  const paymentsStats = useMemo(
    () => [
      {
        title: "Total Collections",
        value:
          filteredPayments?.reduce((acc, curr) => {
            const byCurrency = acc[curr.currency] || 0;
            acc[curr.currency] = byCurrency + curr.amount;
            return acc;
          }, {}) || {},
        icon: IndianRupee,
        description: "Total amount collected",
        isCurrencyGroup: true,
      },
      {
        title: "Verified Payments",
        value: filteredPayments?.filter((p) => p.verified).length || 0,
        icon: Check,
        description: "Number of verified payments",
      },
      {
        title: "Pending Verification",
        value: filteredPayments?.filter((p) => !p.verified).length || 0,
        icon: Clock,
        description: "Payments awaiting verification",
      },
    ],
    [filteredPayments],
  );

  if (isLoading) {
    return (
      <div className="flex h-[200px] w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="space-y-5"
      >
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">HAF Payments</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Manage Hudawi Annual Fund payments
            </p>
          </div>

          <TabsList className="h-9">
            <TabsTrigger value="payments" className="text-xs">
              Payments
            </TabsTrigger>
            <TabsTrigger value="statistics" className="text-xs">
              Statistics
            </TabsTrigger>
          </TabsList>
        </div>{" "}
        <TabsContent value="payments" className="space-y-4">
          {/* Desktop Filters */}
          <div className="hidden md:flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <DatePicker
                selected={dateRange.from}
                onSelect={(date) => handleDateRangeChange("from", date)}
                placeholder="From"
                className="w-auto"
              />
              <DatePicker
                selected={dateRange.to}
                onSelect={(date) => handleDateRangeChange("to", date)}
                placeholder="To"
                className="w-auto"
              />
              <Select value={selectedBatch} onValueChange={handleBatchChange}>
                <SelectTrigger className="w-[150px] h-9 text-xs">
                  <SelectValue placeholder="Filter batch" />
                </SelectTrigger>
                <SelectContent>
                  {batches.map((batch) => (
                    <SelectItem key={batch} value={batch}>
                      {batch === "all" ? "All Batches" : batch}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={sortOrder} onValueChange={handleSortChange}>
                <SelectTrigger className="w-[130px] h-9 text-xs">
                  <SelectValue placeholder="Sort" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="desc">Newest First</SelectItem>
                  <SelectItem value="asc">Oldest First</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={downloadExcel}
                disabled={isDownloadingExcel}
              >
                <Download className="h-3.5 w-3.5" />
                {isDownloadingExcel ? "Generating…" : "Export"}
              </Button>
              <Button
                size="sm"
                className="gap-1.5"
                onClick={() => setOpen(true)}
              >
                <Plus className="h-3.5 w-3.5" /> Add Payment
              </Button>
            </div>
          </div>{" "}
          <div className="grid gap-3 grid-cols-3">
            {paymentsStats.map((stat, i) => (
              <Card key={stat.title} className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground font-medium">
                        {stat.title}
                      </p>
                      {stat.isCurrencyGroup ? (
                        <div className="mt-1.5 space-y-0.5">
                          {Object.entries(stat.value).map(
                            ([currency, amount]) => (
                              <div
                                key={currency}
                                className="flex items-baseline gap-1.5"
                              >
                                <span className="text-[11px] text-muted-foreground">
                                  {currency}
                                </span>
                                <span className="text-base font-bold tabular-nums">
                                  {currencies[currency].symbol}
                                  {amount.toLocaleString()}
                                </span>
                              </div>
                            ),
                          )}
                        </div>
                      ) : (
                        <>
                          <p className="text-xl font-bold mt-1 tabular-nums">
                            {stat.value}
                          </p>
                          <p className="text-[11px] text-muted-foreground mt-0.5">
                            {stat.description}
                          </p>
                        </>
                      )}
                    </div>
                    <div
                      className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${
                        i === 0
                          ? "text-emerald-600 bg-emerald-50"
                          : i === 1
                            ? "text-blue-600 bg-blue-50"
                            : "text-amber-600 bg-amber-50"
                      }`}
                    >
                      <stat.icon className="h-4 w-4" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>{" "}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold">
                HAF Payments
              </CardTitle>
              <span className="text-xs text-muted-foreground">
                {displayedPayments.length} of {filteredPayments.length}
                {hasMore && " · scroll for more"}
              </span>
            </CardHeader>
            <CardContent className="p-0">
              <div className="p-4 border-b">
                <input
                  type="text"
                  placeholder="Search by payment ID, payee, phone, email, chapter or batch…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                />
              </div>
              <HaafPaymentsTable
                data={displayedPayments}
                onVerificationToggle={handleVerificationToggle}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onDownloadReceipt={handleDownloadReceipt}
                showActions={true}
              />

              {isLoadingMore && (
                <div className="flex justify-center items-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-xs text-muted-foreground">
                    Loading more…
                  </span>
                </div>
              )}

              {!hasMore && filteredPayments.length > 0 && (
                <p className="text-center py-3 text-xs text-muted-foreground border-t">
                  No more payments to load
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="statistics" className="space-y-5">
          {isBatchStatsLoading || isChapterStatsLoading ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Tabs defaultValue="batch" className="space-y-4">
              <TabsList className="h-9">
                <TabsTrigger value="batch" className="text-xs">
                  Batch Statistics
                </TabsTrigger>
                <TabsTrigger value="chapter" className="text-xs">
                  Chapter Statistics
                </TabsTrigger>
              </TabsList>

              <TabsContent value="batch">
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <BarChart3 className="h-4 w-4 text-muted-foreground" />
                      Verified Payments by Batch
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ChartContainer
                      config={{
                        amount: {
                          label: "Verified Amount",
                          color: "hsl(217, 91%, 60%)",
                        },
                      }}
                      className="h-[600px]"
                    >
                      <BarChart
                        data={
                          batchStats?.map((stat) => ({
                            name: stat._id || "Unknown Batch",
                            amount: stat.totalVerifiedAmount,
                          })) || []
                        }
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="name"
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          interval={0}
                        />
                        <YAxis
                          tickFormatter={(value) =>
                            `₹${value.toLocaleString()}`
                          }
                        />
                        <ChartTooltip
                          content={<ChartTooltipContent />}
                          formatter={(value) => [
                            `₹${value.toLocaleString()}`,
                            "Verified Amount",
                          ]}
                        />
                        <Bar
                          dataKey="amount"
                          fill="var(--color-amount)"
                          name="Verified Amount"
                        />
                      </BarChart>
                    </ChartContainer>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="chapter">
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <BarChart3 className="h-4 w-4 text-muted-foreground" />
                      Verified Payments by Chapter
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ChartContainer
                      config={{
                        amount: {
                          label: "Verified Amount",
                          color: "hsl(142, 69%, 58%)",
                        },
                      }}
                      className="h-[600px]"
                    >
                      <BarChart
                        data={
                          chapterStats?.map((stat) => ({
                            name: stat.chapterName || "Unknown Chapter",
                            amount: stat.totalVerifiedAmount,
                          })) || []
                        }
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="name"
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          interval={0}
                          fontSize={12}
                        />
                        <YAxis
                          tickFormatter={(value) =>
                            `₹${value.toLocaleString()}`
                          }
                        />
                        <ChartTooltip
                          content={<ChartTooltipContent />}
                          formatter={(value) => [
                            `₹${value.toLocaleString()}`,
                            "Verified Amount",
                          ]}
                        />
                        <Bar
                          dataKey="amount"
                          fill="var(--color-amount)"
                          name="Verified Amount"
                        />
                      </BarChart>
                    </ChartContainer>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          )}
        </TabsContent>
      </Tabs>

      {/* Mobile FABs */}
      <div className="fixed bottom-20 right-4 flex flex-col gap-2 md:hidden">
        <Button
          onClick={downloadExcel}
          disabled={isDownloadingExcel}
          variant="outline"
          className="h-11 w-11 rounded-full shadow-md"
          size="icon"
        >
          <Download className="h-4 w-4" />
        </Button>
        <Button
          onClick={() => setOpen(true)}
          className="h-12 w-12 rounded-full shadow-lg"
          size="icon"
        >
          <Plus className="h-5 w-5" />
        </Button>
      </div>

      {/* Add HAF Payment Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl p-4 pt-6">
          <DialogHeader className="pb-4">
            <DialogTitle>Add New HAF Payment</DialogTitle>
            <DialogDescription className="text-xs">
              Enter the HAF payment details
            </DialogDescription>
          </DialogHeader>
          <AddHaafPaymentForm
            onSuccess={() => {
              setOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Edit HAF Payment Dialog */}
      <Dialog
        open={!!editPayment}
        onOpenChange={(open) => !open && setEditPayment(null)}
      >
        <DialogContent className="max-w-2xl p-4 pt-6">
          <DialogHeader className="pb-4">
            <DialogTitle>Edit HAF Payment</DialogTitle>
            <DialogDescription className="text-xs">
              Update the HAF payment details
            </DialogDescription>
          </DialogHeader>
          {editPayment && (
            <AddHaafPaymentForm
              initialData={editPayment}
              onSuccess={() => {
                setEditPayment(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Receipt Modal */}
      <Dialog open={receiptModalOpen} onOpenChange={handleReceiptClose}>
        <DialogContent className="max-w-3xl p-4 pt-6">
          <DialogHeader className="pb-4">
            <DialogTitle>Payment Receipt</DialogTitle>
            <DialogDescription className="text-xs">
              View and download the payment receipt
            </DialogDescription>
          </DialogHeader>
          {selectedReceipt && (
            <div className="space-y-3">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Payment ID</span>
                  <span className="font-medium">
                    {selectedReceipt.paymentId}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="font-medium">
                    {currencies[selectedReceipt.currency].symbol}
                    {selectedReceipt.amount.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Currency</span>
                  <span className="font-medium">
                    {selectedReceipt.currency}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Date</span>
                  <span className="font-medium">
                    {new Date(selectedReceipt.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
              <Button
                onClick={() => console.log("Download receipt clicked")}
                className="w-full"
                size="sm"
              >
                Download Receipt
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Haaf Receipt Modal */}
      <HaafReceiptModal
        isOpen={!!receiptPayment}
        onClose={() => setReceiptPayment(null)}
        payment={receiptPayment}
      />
    </div>
  );
}
